'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/inputs/bulk-action/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { BulkActionSelect } from 'BomdisoftComponents/inputs/bulk-action/selects/single';
import { Select } from 'BomdisoftComponents/inputs/field/select/index';
import { BulkActionSelectOption } from 'BomdisoftComponents/types';
import { bulkAction, change, getLanguageFromLocale, keepCurrent, undo } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { type ChangeEvent, useState } from 'react';

type BulkActionsChangeInputProps = {
  options: BulkActionSelectOption[];
  selected: BulkActionSelectOption | null;
  selectHandler: (newSelectedItem: BulkActionSelectOption | null) => void;
  placeholder: string;
  noOptionsText: string;
  className?: string;
};

type BulkActionChangeType = 'keep-current' | 'change';
type BulkAction = {
  id: BulkActionChangeType;
  name: string;
};

export const BulkActionsChangeInput = ({ options, selected, selectHandler, placeholder, noOptionsText, className }: BulkActionsChangeInputProps) => {
  const params = useParams();
  const currentLanguage = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const [selectedAction, setSelectedAction] = useState<BulkActionChangeType>('keep-current');
  const actions: BulkAction[] = [
    { id: 'keep-current', name: keepCurrent[currentLanguage] },
    { id: 'change', name: change[currentLanguage] },
  ];

  const reset = () => {
    setSelectedAction('keep-current');
    selectHandler(null);
  };

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedAction(event.target.value as BulkActionChangeType);
    selectHandler(null);
  };

  return (
    <div className={joinClasses('bomdisoft-bulk-action-container', className)}>
      <Select aria-label={bulkAction[currentLanguage]} className='bomdisoft-bulk-action-action-select' onChange={handleChange} size='small' testId='select-bulk-action' value={selectedAction}>
        {actions.map(action => (
          <option key={action.id} value={action.id}>
            {action.name}
          </option>
        ))}
      </Select>
      {selectedAction === 'change' && (
        <>
          <BulkActionSelect noOptionsText={noOptionsText} options={options} placeholder={placeholder} selected={selected} selectHandler={selectHandler} />
          <button className='bomdisoft-bulk-action-undo' onClick={reset} type='button'>
            {undo[currentLanguage]}
          </button>
        </>
      )}
    </div>
  );
};
